import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import HeaderCustom from "../../../components/HeaderCustom";
import { Backdrop, CircularProgress } from "@mui/material";
import "../../../styles/AppCss.css";
import { getFiltrarCompras, getLocales } from "../../../api/ApiService";
import * as XLSX from "xlsx";
import FitrosLocales from "./components/FitrosLocales";
import { useNavigate } from "react-router-dom";
import ModalQrComponents from "./components/ModalQrComponents";
import ComprasHeader from "./components/ComprasHeader";
import ComprasTableComponent from "./components/ComprasTableComponent";

function ComprasPage() {
    const [buscar, setBuscar] = useState("");
    const [empresaSelected, setEmpresaSelected] = useState(null);
    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");
    const [loading, setLoading] = useState(false);
    const [dataLocales, setDataLocales] = useState([]);
    const [empresasOptions, setEmpresasOptions] = useState([]);
    const [loadingCargar, setLoadingCargar] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [localSelected, setLocalSelected] = useState(null);
    const [loadingDescargar, setLoadingDescargar] = useState(false);
    const [loadingXlsx, setLoadingXlsx] = useState(false);

    const [metaPaginacion, setMetaPaginacion] = useState({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
    });
    const [perPage, setPerPage] = useState(15);

    const stateToken = useSelector((state) => state.reducerGlobal.token);
    let history = useNavigate();

    useEffect(() => {
        if (stateToken?.access_token) {
            handleListarLocales();
            handleApiFiltrarCompras(1);
        }
    }, [stateToken]);

    useEffect(() => {
        if (!stateToken?.access_token) {
            return;
        }

        const timeOutId = setTimeout(() => {
            handleApiFiltrarCompras(1);
        }, 600);

        return () => clearTimeout(timeOutId);
    }, [stateToken, buscar, empresaSelected, fechaDesde, fechaHasta, perPage]);

    // Funcion para filtrar compras por los parametros del endpoint.
    const handleApiFiltrarCompras = (page = 1) => {
        const body = {
            page: page,
            per_page: perPage,
            buscar: buscar,
            empresa_id: empresaSelected?.id || "",
            fecha_desde: fechaDesde,
            fecha_hasta: fechaHasta,
        };

        setLoadingCargar(true);

        getFiltrarCompras(stateToken?.access_token, body)
            .then((response) => {
                // console.log("response==>getFiltrarCompras", response?.data);
                const registros = Array.isArray(response?.data) ? response.data : [];

                setDataLocales(response?.data?.data || []);
                setLoading(true);

                const total = response?.total ?? registros.length;
                const perPageResponse = response?.per_page ?? perPage;
                const currentPage = response?.current_page ?? page;
                const lastPage = response?.last_page ?? Math.max(1, Math.ceil(total / perPageResponse));

                setMetaPaginacion({
                    current_page: currentPage,
                    per_page: perPageResponse,
                    total: total,
                    last_page: lastPage,
                });

                setLoadingCargar(false);
            })
            .catch((error) => {
                console.log("error==>getFiltrarCompras", error);
                setDataLocales([]);
                setMetaPaginacion({
                    current_page: 1,
                    per_page: perPage,
                    total: 0,
                    last_page: 1,
                });
                setLoading(true);
                setLoadingCargar(false);
            });
    };

    // Lista empresas para el selector del filtro empresa_id.
    const handleListarLocales = () => {
        getLocales(stateToken?.access_token)
            .then((response) => {
                if (response?.data?.length > 0) {
                    setEmpresasOptions(response.data);
                } else {
                    setEmpresasOptions([]);
                }
            })
            .catch((error) => {
                console.log("error-getLocales", error);
                setEmpresasOptions([]);
            });
    };

    const handleBuscar = (value) => {
        setBuscar(value);
    };

    const handleEmpresaSelected = (value) => {
        setEmpresaSelected(value);
    };

    const handleFechaDesde = (value) => {
        setFechaDesde(value);
    };

    const handleFechaHasta = (value) => {
        setFechaHasta(value);
    };

    const handleQrClick = (item) => {
        setOpenModal(true);
        setLocalSelected(item);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setLocalSelected(null);
    };

    const handleEditClick = (item) => {
        history(`/app/admin/local-editar/${item?.id}`);
    };

    const handleVerLocal = (item) => {
        history(`/app/admin/local-detalle/${item?.id}?mode=view`);
    };

    const handleAddCompra = () => {
        history("/app/admin/compras/agregar");
    };

    const handlePerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
    };

    const handleDescargarXlsx = () => {
        if (!dataLocales || dataLocales.length === 0) {
            alert("No hay datos para exportar.");
            return;
        }

        const filas = dataLocales.map((item) => ({
            "Fecha Compra": item?.fecha_compra || "-",
            "Monto Compra": item?.monto_compra !== undefined && item?.monto_compra !== null
                ? Number(item.monto_compra)
                : "-",
            "Es Gentileza": Number(item?.es_gentileza) === 1 ? "Si" : "No",
            "Observación": item?.observacion || "-",
            "Número Comprobante": item?.numero_comprobante || "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(filas);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Compras");

        const fecha = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `compras_${fecha}.xlsx`);
    };

    return (
        <HeaderCustom>
            <div className="min-h-screen bg-gray-100 p-6">
                {loading ? (
                    <div>
                        <ComprasHeader
                            title="Compras"
                            subtitle="Administra y supervisa las compras de la plataforma."
                            handleAdd={handleAddCompra}
                            handleDescargarXlsx={handleDescargarXlsx}
                        />

                        <div>
                            <FitrosLocales
                                buscar={buscar}
                                handleBuscar={handleBuscar}
                                empresaSelected={empresaSelected}
                                handleEmpresaSelected={handleEmpresaSelected}
                                fechaDesde={fechaDesde}
                                handleFechaDesde={handleFechaDesde}
                                fechaHasta={fechaHasta}
                                handleFechaHasta={handleFechaHasta}
                                dataLocales={empresasOptions}
                            />

                            <ComprasTableComponent
                                data={dataLocales}
                                metaPaginacion={metaPaginacion}
                                onPageChange={handleApiFiltrarCompras}
                                perPage={perPage}
                                onPerPageChange={handlePerPageChange}
                                handleEditarItem={handleEditClick}
                                handleVerLocal={handleVerLocal}
                                handleQrClick={handleQrClick}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md p-8">
                        <div className="h-6 bg-gray-200 rounded w-1/3 mb-6 animate-pulse" />
                        <div className="space-y-4">
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                            <div className="h-24 bg-gray-200 rounded animate-pulse" />
                        </div>
                    </div>
                )}

                <ModalQrComponents
                    openModal={openModal}
                    loading={loadingDescargar}
                    handleCloseModal={handleCloseModal}
                    localSelected={localSelected}
                />

                <Backdrop open={loadingCargar} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </HeaderCustom>
    );
}

export default ComprasPage;
